import React, { useState, useCallback, useMemo, useEffect } from 'react'
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Divider,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material'
import {
  ContentPaste,
  PlayArrow,
  Clear,
  Download,
  Science,
  ExpandMore,
  Description,
} from '@mui/icons-material'
import FileViewer from 'molecules/FileViewer/FileViewer'
import FileUploader from 'molecules/FileUploader/FileUploader'
import ContentTypeTag from 'atoms/ContentTypeTag/ContentTypeTag'
import { useTranslationMapDetailsContext } from '../../contexts/TranslationMapDetailsContext'
import { usePreviewTranslationMap } from '../../api'
import { toast } from 'atoms/Toast'
import { safeBase64Encode } from 'common/helpers/encoding'
import { useListDataModelSchemaResources } from 'pages/DataModelSchema/api'
import { previewDataModelSchemaResource } from 'services/kraken-core/data_model/dataModel.service'
import { DATA_MODEL_SCHEMA_RESOURCE_KINDS } from 'common/types/kraken-core/DataModelSchemaResource'

// Constants
const FILE_VIEWER_HEIGHT = 700

export interface MappingPreviewProps {
  isEnabled: boolean
}

interface TranslateState {
  inputPayload: string
  outputPayload: string
  translateError: string | null
  activeTab: number
  executionTime: number | null
}

interface ValidationResource {
  id: string
  name: string
  content_type: string
}

interface HttpError extends Error {
  response?: {
    status: number
    data?: {
      message?: string
    }
  }
}

const MappingPreview: React.FC<MappingPreviewProps> = ({ isEnabled }) => {
  const { translationMap, dataModels } = useTranslationMapDetailsContext()
  const {
    mutateAsync: previewTranslationMap,
    isLoading: isTranslating,
  } = usePreviewTranslationMap()

  const [state, setState] = useState<TranslateState>({
    inputPayload: '',
    outputPayload: '',
    translateError: null,
    activeTab: 0,
    executionTime: null,
  })

  // Validation resources state
  const [validationResources, setValidationResources] = useState<ValidationResource[]>([])
  const [isLoadingValidationResources, setIsLoadingValidationResources] = useState(false)
  const [validationMenuAnchor, setValidationMenuAnchor] = useState<null | HTMLElement>(null)
  const [isLoadingResourceContent, setIsLoadingResourceContent] = useState(false)

  // Fetch validation resources for input data model
  const {
    data: validationResourcesData,
    isLoading: isLoadingValidationResourcesData,
  } = useListDataModelSchemaResources(
    {
      model_id: dataModels.input?.dataModel?.id || '',
    },
    {
      enabled: !!dataModels.input?.dataModel?.id,
    }
  )

  // Process validation resources when data is loaded
  useEffect(() => {
    if (validationResourcesData?.results) {
      const validationResources = validationResourcesData.results
        .filter(resource => resource.kind === DATA_MODEL_SCHEMA_RESOURCE_KINDS.VALIDATION)
        .map(resource => ({
          id: resource.id,
          name: resource.name,
          content_type: resource.content_type,
        }))
      setValidationResources(validationResources)
    }
  }, [validationResourcesData])

  // Determine content type for output based on output configuration
  const outputContentType = useMemo(() => {
    if (!translationMap?.output_content_type) return 'application/json'

    // Map common content types to FileViewer supported types
    const contentTypeMap: Record<string, string> = {
      'application/json': 'application/json',
      'application/xml': 'application/xml',
      'text/xml': 'text/xml',
      'text/plain': 'text',
      'application/edi-x12': 'application/EDI-X12',
      'application/x12': 'application/x12',
      'text/csv': 'text/csv',
    }

    return contentTypeMap[translationMap.output_content_type] || 'application/json'
  }, [translationMap?.output_content_type])

  const handleFileUpload = useCallback((files: File[]) => {
    if (files.length === 0) return

    const file = files[0]
    const reader = new FileReader()

    reader.onload = e => {
      const content = e.target?.result as string
      setState(prev => ({
        ...prev,
        inputPayload: content,
        translateError: null,
      }))
    }

    reader.onerror = () => {
      toast.error('Error reading file')
    }

    reader.readAsText(file)
  }, [])

  const handleInputChange = useCallback((value: string) => {
    setState(prev => ({
      ...prev,
      inputPayload: value,
      translateError: null,
    }))
  }, [])

  const handleClearInput = useCallback(() => {
    setState(prev => ({
      ...prev,
      inputPayload: '',
      outputPayload: '',
      translateError: null,
      executionTime: null,
    }))
  }, [])

  const handleTranslate = useCallback(async () => {
    if (!translationMap?.id || !state.inputPayload.trim()) {
      toast.error('Please provide input payload')
      return
    }

    const startTime = performance.now()

    setState(prev => ({
      ...prev,
      translateError: null,
    }))

    try {
      const response = await previewTranslationMap({
        id: translationMap.id,
        data: {
          input: {
            content: safeBase64Encode(state.inputPayload),
            content_type: translationMap.input_content_type || 'application/json',
          },
        },
      })

      const endTime = performance.now()
      const executionTime = Math.round(endTime - startTime)

      if (response?.translated_payload) {
        try {
          // Decode the base64 encoded translated payload
          const decodedPayload = atob(response.translated_payload)
          setState(prev => ({
            ...prev,
            outputPayload: decodedPayload,
            activeTab: 1, // Switch to output tab
            executionTime,
          }))
        } catch (decodeError) {
          setState(prev => ({
            ...prev,
            translateError: 'Failed to decode translated payload',
            executionTime,
          }))
          toast.error('Translation failed: Invalid payload format')
        }
      } else {
        setState(prev => ({
          ...prev,
          translateError: 'No translated payload received',
          executionTime,
        }))
        toast.error('Translation failed: No output received')
      }
    } catch (error) {
      const endTime = performance.now()
      const executionTime = Math.round(endTime - startTime)

      let errorMessage = 'Unknown error occurred'

      // Handle different error types
      if (error instanceof Error) {
        const httpError = error as HttpError
        // Check if it's an HTTP error with response data
        if (
          httpError.response &&
          httpError.response.status === 400 &&
          httpError.response.data?.message
        ) {
          errorMessage = httpError.response.data.message
        } else {
          errorMessage = error.message
        }
      } else if (
        error &&
        typeof error === 'object' &&
        'message' in error &&
        typeof error.message === 'string'
      ) {
        errorMessage = error.message
      }

      setState(prev => ({
        ...prev,
        translateError: errorMessage,
        activeTab: 0, // Switch to input tab to show error
        executionTime,
      }))
    }
  }, [
    translationMap?.id,
    translationMap?.input_content_type,
    state.inputPayload,
    previewTranslationMap,
  ])

  const handleDownloadOutput = useCallback(() => {
    if (!state.outputPayload) return

    const blob = new Blob([state.outputPayload], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `translation-output-${new Date().toISOString().split('T')[0]}.txt`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }, [state.outputPayload])

  const handleCreateTest = useCallback(() => {
    if (!state.outputPayload) return

    // TODO: Implement create test functionality
    toast.info('Create test functionality will be implemented soon')
  }, [state.outputPayload])

  const handleTabChange = useCallback((event: React.SyntheticEvent, newValue: number) => {
    setState(prev => ({
      ...prev,
      activeTab: newValue,
    }))
  }, [])

  // Validation menu handlers
  const handleValidationMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setValidationMenuAnchor(event.currentTarget)
  }, [])

  const handleValidationMenuClose = useCallback(() => {
    setValidationMenuAnchor(null)
  }, [])

  const handleValidationResourceSelect = useCallback(async (resource: ValidationResource) => {
    setValidationMenuAnchor(null)
    setIsLoadingResourceContent(true)

    try {
      const { response } = await previewDataModelSchemaResource(resource.id)
      if (response?.data?.content) {
        setState(prev => ({
          ...prev,
          inputPayload: response.data.content,
          translateError: null,
        }))
      } else {
        toast.error('Failed to load resource content')
      }
    } catch (error) {
      toast.error('Failed to load validation resource')
    } finally {
      setIsLoadingResourceContent(false)
    }
  }, [])

  return (
    <Card
      sx={{
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        // Enhanced visual states based on content
        border: state.translateError ? '2px solid' : '1px solid',
        borderColor: state.translateError ? 'error.main' : 'divider',
        ...(state.outputPayload &&
          !state.translateError && {
            borderColor: 'success.main',
            boxShadow: '0 0 0 2px rgba(76, 175, 80, 0.1)',
          }),
        // Add subtle animation for successful translations
        ...(state.outputPayload &&
          !state.translateError && {
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '3px',
              background: 'linear-gradient(90deg, success.main, success.light)',
              animation: 'success-pulse 2s ease-in-out',
              '@keyframes success-pulse': {
                '0%': { opacity: 0 },
                '50%': { opacity: 1 },
                '100%': { opacity: 0.7 },
              },
            },
          }),
      }}
    >
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6">Preview</Typography>
            {/* Status indicator */}
            {state.outputPayload && !state.translateError && (
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: 'success.main',
                  animation: 'pulse 2s infinite',
                  '@keyframes pulse': {
                    '0%': { opacity: 1 },
                    '50%': { opacity: 0.5 },
                    '100%': { opacity: 1 },
                  },
                }}
              />
            )}
            {state.translateError && (
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: 'error.main',
                }}
              />
            )}
            {/* Execution time display */}
            {state.executionTime !== null && (
              <Chip
                label={`${state.executionTime}ms`}
                size="small"
                sx={{
                  backgroundColor: state.translateError ? 'error.light' : 'success.light',
                  color: state.translateError ? 'error.contrastText' : 'success.contrastText',
                  fontSize: '0.75rem',
                  height: '24px',
                }}
              />
            )}
          </Box>
        }
        action={
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Clear />}
              onClick={handleClearInput}
              disabled={!state.inputPayload && !state.outputPayload}
              sx={{
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: 2,
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              Clear
            </Button>
            {state.outputPayload && (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Science />}
                  onClick={handleCreateTest}
                  sx={{
                    backgroundColor: 'primary.light',
                    borderColor: 'primary.main',
                    color: 'primary.contrastText',
                    '&:hover': {
                      backgroundColor: 'primary.main',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  Create test
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Download />}
                  onClick={handleDownloadOutput}
                  sx={{
                    backgroundColor: 'success.light',
                    borderColor: 'success.main',
                    color: 'success.contrastText',
                    '&:hover': {
                      backgroundColor: 'success.main',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  Download
                </Button>
              </>
            )}
          </Stack>
        }
      />
      <Divider />

      <CardContent sx={{ p: 0, height: 'calc(100% - 80px)' }}>
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Content Tabs */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Tabs
              value={state.activeTab}
              onChange={handleTabChange}
              variant="fullWidth"
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                '& .MuiTab-root': {
                  position: 'relative',
                  fontWeight: 600,
                  textTransform: 'none',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    transform: 'translateY(-1px)',
                  },
                  '&.Mui-selected': {
                    color: 'primary.main',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '60%',
                      height: 3,
                      backgroundColor: 'primary.main',
                      borderRadius: '2px 2px 0 0',
                      boxShadow: '0 2px 8px rgba(51, 204, 96, 0.3)',
                    },
                  },
                  '&.Mui-disabled': {
                    opacity: 0.5,
                    '&:hover': {
                      transform: 'none',
                      backgroundColor: 'transparent',
                    },
                  },
                },
                '& .MuiTabs-indicator': {
                  height: 3,
                  borderRadius: '2px 2px 0 0',
                  background: 'linear-gradient(90deg, primary.main, primary.light)',
                  boxShadow: '0 2px 8px rgba(51, 204, 96, 0.3)',
                },
              }}
            >
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    Input
                    {translationMap?.input_content_type && (
                      <ContentTypeTag
                        contentType={translationMap.input_content_type}
                        size="small"
                      />
                    )}
                  </Box>
                }
                sx={{
                  '&.Mui-selected': {
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '2px',
                      background: 'linear-gradient(90deg, primary.main, primary.light)',
                      opacity: 0.6,
                    },
                  },
                }}
              />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    Output
                    {translationMap?.output_content_type && (
                      <ContentTypeTag
                        contentType={translationMap.output_content_type}
                        size="small"
                      />
                    )}
                  </Box>
                }
                disabled={!state.outputPayload}
                sx={{
                  '&.Mui-selected': {
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '2px',
                      background: 'linear-gradient(90deg, success.main, success.light)',
                      opacity: 0.6,
                    },
                  },
                }}
              />
            </Tabs>

            <Box sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column' }}>
              {/* Input Tab */}
              {state.activeTab === 0 && (
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  {/* Error Display */}
                  {state.translateError && (
                    <Alert
                      severity="error"
                      sx={{
                        mb: 2,
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '3px',
                          background: 'linear-gradient(90deg, error.main, error.light)',
                        },
                        '& .MuiAlert-icon': {
                          animation: 'shake 0.5s ease-in-out',
                          '@keyframes shake': {
                            '0%, 100%': { transform: 'translateX(0)' },
                            '25%': { transform: 'translateX(-2px)' },
                            '75%': { transform: 'translateX(2px)' },
                          },
                        },
                      }}
                    >
                      {state.translateError}
                    </Alert>
                  )}

                  {/* Upload and Paste Options - Compact */}
                  <Stack direction="row" spacing={1} sx={{ mb: 2, alignItems: 'center' }}>
                    {/* Compact File Upload */}
                    <Box
                      sx={{
                        '& > div > div': {
                          minHeight: '40px !important',
                          padding: '8px 16px !important',
                          fontSize: '0.875rem',
                          '& p': {
                            fontSize: '0.875rem',
                            margin: '0 !important',
                          },
                        },
                      }}
                    >
                      <FileUploader handleUpload={handleFileUpload} multiple={false} />
                    </Box>
                    <Button
                      variant="outlined"
                      startIcon={<ContentPaste />}
                      onClick={() => {
                        navigator.clipboard
                          .readText()
                          .then(text => {
                            handleInputChange(text)
                          })
                          .catch(() => {
                            toast.error('Failed to read from clipboard')
                          })
                      }}
                      sx={{
                        minWidth: 'auto',
                        px: 2,
                        height: '40px',
                        fontSize: '0.875rem',
                      }}
                    >
                      Paste
                    </Button>
                    {/* Validation Resources Button */}
                    {validationResources.length > 0 && (
                      <Button
                        variant="outlined"
                        startIcon={
                          isLoadingResourceContent ? (
                            <CircularProgress size={16} />
                          ) : (
                            <Description />
                          )
                        }
                        endIcon={<ExpandMore />}
                        onClick={handleValidationMenuOpen}
                        disabled={isLoadingResourceContent}
                        sx={{
                          minWidth: 'auto',
                          px: 2,
                          height: '40px',
                          fontSize: '0.875rem',
                          backgroundColor: 'primary.light',
                          borderColor: 'primary.main',
                          color: 'primary.contrastText',
                          '&:hover': {
                            backgroundColor: 'primary.main',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                          },
                          transition: 'all 0.2s ease-in-out',
                        }}
                      >
                        Samples
                        <Chip
                          label={validationResources.length}
                          size="small"
                          sx={{
                            ml: 1,
                            height: '20px',
                            fontSize: '0.75rem',
                            backgroundColor: 'primary.main',
                            color: 'primary.contrastText',
                          }}
                        />
                      </Button>
                    )}
                    <Button
                      variant="contained"
                      startIcon={isTranslating ? <CircularProgress size={16} /> : <PlayArrow />}
                      onClick={handleTranslate}
                      disabled={!state.inputPayload.trim() || isTranslating}
                      sx={{
                        ml: 'auto',
                        height: '40px',
                        fontSize: '0.875rem',
                        position: 'relative',
                        overflow: 'hidden',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 6px 20px rgba(51, 204, 96, 0.4)',
                        },
                        '&:active': {
                          transform: 'translateY(0)',
                        },
                        '&:disabled': {
                          transform: 'none',
                          boxShadow: 'none',
                        },
                        // Add subtle animation for enabled state
                        ...(!isTranslating &&
                          state.inputPayload.trim() && {
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: '-100%',
                              width: '100%',
                              height: '100%',
                              background:
                                'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
                              animation: 'shimmer 3s infinite',
                              '@keyframes shimmer': {
                                '0%': { left: '-100%' },
                                '100%': { left: '100%' },
                              },
                            },
                          }),
                        transition: 'all 0.3s ease-in-out',
                      }}
                    >
                      {isTranslating ? 'Translating...' : 'Translate'}
                    </Button>
                  </Stack>

                  {/* Input Content */}
                  <Box sx={{ flex: 1 }}>
                    <FileViewer
                      content={state.inputPayload}
                      contentType={translationMap.input_content_type}
                      onChange={handleInputChange}
                      options={{
                        heigth: FILE_VIEWER_HEIGHT,
                        showValidations: true,
                      }}
                      showOptions={false}
                    />
                  </Box>
                </Box>
              )}

              {/* Output Tab */}
              {state.activeTab === 1 && (
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  {state.translateError ? (
                    <Alert
                      severity="error"
                      sx={{
                        mb: 2,
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '3px',
                          background: 'linear-gradient(90deg, error.main, error.light)',
                        },
                        '& .MuiAlert-icon': {
                          animation: 'shake 0.5s ease-in-out',
                          '@keyframes shake': {
                            '0%, 100%': { transform: 'translateX(0)' },
                            '25%': { transform: 'translateX(-2px)' },
                            '75%': { transform: 'translateX(2px)' },
                          },
                        },
                      }}
                    >
                      {state.translateError}
                    </Alert>
                  ) : (
                    <Box sx={{ flex: 1 }}>
                      <FileViewer
                        content={state.outputPayload}
                        contentType={translationMap.output_content_type}
                        options={{
                          heigth: FILE_VIEWER_HEIGHT,
                          showValidations: true,
                        }}
                        showOptions={false}
                      />
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </CardContent>

      {/* Validation Resources Menu */}
      <Menu
        anchorEl={validationMenuAnchor}
        open={Boolean(validationMenuAnchor)}
        onClose={handleValidationMenuClose}
        PaperProps={{
          sx: {
            maxHeight: 300,
            minWidth: 250,
            '& .MuiMenuItem-root': {
              py: 1,
            },
          },
        }}
      >
        {validationResources.map(resource => (
          <MenuItem
            key={resource.id}
            onClick={() => handleValidationResourceSelect(resource)}
            disabled={isLoadingResourceContent}
          >
            <ListItemIcon>
              <Description fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={resource.name}
              secondary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <ContentTypeTag contentType={resource.content_type} size="small" />
                </Box>
              }
            />
          </MenuItem>
        ))}
      </Menu>
    </Card>
  )
}

export default MappingPreview
